'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { webPagesClient } from '@terra/shared-rest-clients-web-pages';
import type { WebPageFeatureReturnDto } from '@terra/shared-contracts-web-pages';
import { isFeatureOverrideRunning } from '../feature-analysis/feature-analysis.utils';
import { toasterApi } from '../../toast/ToastProvider';
import {
  FEATURE_RELATED_REQUESTS_QUERY_KEY,
  WEBPAGE_FEATURES_QUERY_KEY,
} from './applicationAssetsQueryKeys';

export { WEBPAGE_FEATURES_QUERY_KEY } from './applicationAssetsQueryKeys';

type UseWebPageFeaturesParams = {
  webpageId: string | undefined;
  enabled?: boolean;
  overrideStartedAtByFeatureId?: ReadonlyMap<string, number>;
  onFeatureOverrideAcknowledged?: (featureId: string) => void;
};

type FeatureSessionStatus =
  WebPageFeatureReturnDto['latestCrawlerSessionStatus'];

const OVERRIDE_POLL_INTERVAL_MS = 2_000;
const OVERRIDE_POLL_MAX_DURATION_MS = 10 * 60_000;

const isTerminalStatus = (
  status: FeatureSessionStatus,
): status is 'COMPLETED' | 'FAILED' | 'ABORTED' =>
  status === 'COMPLETED' || status === 'FAILED' || status === 'ABORTED';

const fetchWebPageFeatures = async ({
  webpageId,
}: {
  webpageId: string;
}): Promise<WebPageFeatureReturnDto[]> => {
  const response = await webPagesClient.getWebPageFeatures({
    params: { id: webpageId },
  });

  if (response.status === 200) {
    return response.body.features.map((feature) => ({
      ...feature,
      executionBlockLabels: feature.executionBlockLabels ?? [],
      matchedGuardrailIds: feature.matchedGuardrailIds ?? [],
      harEntryIds: feature.harEntryIds ?? [],
      auditTrail: (feature.auditTrail ?? []).map((event) => ({
        ...event,
        occurredAt: new Date(event.occurredAt),
      })),
      createdAt: new Date(feature.createdAt),
      updatedAt: new Date(feature.updatedAt),
    }));
  }

  throw new Error('Failed to load webpage features');
};

export const useWebPageFeatures = ({
  webpageId,
  enabled = true,
  overrideStartedAtByFeatureId,
  onFeatureOverrideAcknowledged,
}: UseWebPageFeaturesParams) => {
  const queryClient = useQueryClient();
  const previousStatusesRef = useRef(new Map<string, FeatureSessionStatus>());
  const handledTerminalRunsRef = useRef(new Set<string>());
  const pollingStartedAtRef = useRef<number | null>(null);
  const t = useTranslations(
    'designSystem.applicationAssets.drawer.features.overrideRun.toast',
  );
  const query = useQuery({
    queryKey: [WEBPAGE_FEATURES_QUERY_KEY, webpageId],
    queryFn: () => {
      if (!webpageId) {
        return Promise.resolve<WebPageFeatureReturnDto[]>([]);
      }

      return fetchWebPageFeatures({ webpageId });
    },
    enabled: enabled && Boolean(webpageId),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    refetchInterval: (featuresQuery) => {
      const hasLocallyStartingFeature =
        (overrideStartedAtByFeatureId?.size ?? 0) > 0;
      const hasRunningOverride = featuresQuery.state.data?.some(
        isFeatureOverrideRunning,
      );

      if (!hasLocallyStartingFeature && !hasRunningOverride) {
        pollingStartedAtRef.current = null;

        return false;
      }

      const startedAt = pollingStartedAtRef.current ?? Date.now();

      pollingStartedAtRef.current = startedAt;

      return Date.now() - startedAt > OVERRIDE_POLL_MAX_DURATION_MS
        ? false
        : OVERRIDE_POLL_INTERVAL_MS;
    },
  });
  const features = useMemo(() => query.data ?? [], [query.data]);

  useEffect(() => {
    const nextStatuses = new Map<string, FeatureSessionStatus>();

    features.forEach((feature) => {
      const status = feature.latestCrawlerSessionStatus;

      nextStatuses.set(feature.id, status);

      if (!feature.lastRunIsPolicyOverride || !status) {
        return;
      }

      const hasLocalStart = overrideStartedAtByFeatureId?.has(feature.id);
      const previousStatus = previousStatusesRef.current.get(feature.id);
      const transitionedFromRunning =
        previousStatus === 'RUNNING' || previousStatus === 'ABORTING';
      const isSessionLive = status === 'RUNNING' || status === 'ABORTING';
      const isStartedRunVisible =
        hasLocalStart &&
        (isSessionLive || transitionedFromRunning || feature.isFeatureExecuted);

      if (isStartedRunVisible) {
        onFeatureOverrideAcknowledged?.(feature.id);
      }

      if (!hasLocalStart || !isTerminalStatus(status)) {
        return;
      }

      if (previousStatus === undefined) {
        return;
      }

      const isUnchangedTerminal =
        previousStatus === status && !transitionedFromRunning;

      if (isUnchangedTerminal) {
        return;
      }

      const terminalRunKey = `${feature.id}:${new Date(feature.updatedAt).getTime()}:${status}`;

      if (handledTerminalRunsRef.current.has(terminalRunKey)) {
        return;
      }

      handledTerminalRunsRef.current.add(terminalRunKey);
      onFeatureOverrideAcknowledged?.(feature.id);

      if (status === 'COMPLETED' && feature.isFeatureExecuted) {
        toasterApi.success({
          title: t('completedTitle'),
          description: t('completedDescription'),
        });
      } else {
        toasterApi.error({
          title: t('failedTitle'),
          description: t('failedDescription'),
        });
      }

      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: [WEBPAGE_FEATURES_QUERY_KEY, webpageId],
        }),
        queryClient.invalidateQueries({
          queryKey: [FEATURE_RELATED_REQUESTS_QUERY_KEY, webpageId, feature.id],
        }),
      ]);
    });

    previousStatusesRef.current = nextStatuses;
  }, [
    features,
    overrideStartedAtByFeatureId,
    onFeatureOverrideAcknowledged,
    queryClient,
    t,
    webpageId,
  ]);

  return {
    features,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
